import React from 'react';
import { Loader2, Send, RefreshCw } from 'lucide-react';
import { useConfirm } from '../../contexts/ConfirmContext';

export interface TaxonomyRefreshResult {
  resolved_count: number;
  still_unresolved_count: number;
  taxonomy_diff: {
    added_count: number;
    removed_count: number;
    added_names: string[];
    removed_names: string[];
  };
}

interface BulkActionBarProps {
  unreviewedCount: number;
  total: number;
  onApproveAll: () => void;
  onSubmit: () => void;
  submitting: boolean;
  onRefreshTaxonomy: () => void;
  refreshing: boolean;
  taxonomyRefreshResult?: TaxonomyRefreshResult | null;
}

export const BulkActionBar: React.FC<BulkActionBarProps> = ({
  unreviewedCount,
  total,
  onApproveAll,
  onSubmit,
  submitting,
  onRefreshTaxonomy,
  refreshing,
  taxonomyRefreshResult,
}) => {
  const confirm = useConfirm();
  const handleApproveAll = async () => {
    const ok = await confirm({
      title: `Approve ${unreviewedCount.toLocaleString()} unreviewed part${unreviewedCount === 1 ? '' : 's'}?`,
      description: "Each one takes the category the AI suggested. You can still change them individually afterwards.",
      confirmText: 'Approve all',
    });
    if (!ok) return;
    onApproveAll();
  };

  const handleSubmit = () => {
    onSubmit();
  };

  return (
    <div className="flex flex-col items-start gap-1.5">
      {/* Row 1: Approve All + Submit + count */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleApproveAll}
          disabled={unreviewedCount === 0}
          data-testid="bulk-approve-all-btn"
          className="px-3 py-1.5 text-sm font-medium rounded-lg border border-qc-success/30 text-qc-success hover:bg-qc-success/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Approve All ({unreviewedCount.toLocaleString()})
        </button>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting || unreviewedCount > 0}
          data-testid="submit-validation-btn"
          className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-lg bg-interactive-primary hover:bg-interactive-primary-hover text-text-inverse transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              Submit to digiPART
              {unreviewedCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-interactive-primary rounded-full">
                  {unreviewedCount.toLocaleString()} unreviewed
                </span>
              )}
            </>
          )}
        </button>

        <span className="text-xs text-text-tertiary ml-1">
          {total.toLocaleString()} total parts
        </span>
      </div>

      {/* Row 2: Update Categories (left-aligned, below Approve All) */}
      <div className="flex flex-col items-start gap-1">
        <button
          type="button"
          onClick={onRefreshTaxonomy}
          disabled={refreshing}
          data-testid="refresh-taxonomy-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-border text-text-secondary hover:bg-bg-subtle transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {refreshing ? (
            <><Loader2 className="w-3.5 h-3.5 animate-spin" />Refreshing…</>
          ) : (
            <><RefreshCw className="w-3.5 h-3.5" />Update Categories</>
          )}
        </button>

        {/* Row 3: Taxonomy diff result — shown after a successful refresh */}
        {taxonomyRefreshResult && !refreshing && (
          <div className="flex flex-col gap-0.5 text-xs pl-0.5" data-testid="taxonomy-refresh-result">
            {taxonomyRefreshResult.taxonomy_diff.added_count > 0 ? (
              <span className="text-qc-warning">
                {taxonomyRefreshResult.taxonomy_diff.added_count} new categor{taxonomyRefreshResult.taxonomy_diff.added_count === 1 ? 'y' : 'ies'} added
                {taxonomyRefreshResult.taxonomy_diff.added_names.length > 0 && (
                  <span className="text-text-tertiary">
                    {' '}({taxonomyRefreshResult.taxonomy_diff.added_names.slice(0, 3).join(', ')}
                    {taxonomyRefreshResult.taxonomy_diff.added_count > 3
                      ? ` +${taxonomyRefreshResult.taxonomy_diff.added_count - 3} more`
                      : ''})
                  </span>
                )}
              </span>
            ) : (
              <span className="text-text-tertiary">No new categories — taxonomy is up to date</span>
            )}
            {taxonomyRefreshResult.resolved_count > 0 && (
              <span className="text-qc-success">
                {taxonomyRefreshResult.resolved_count} flagged entr{taxonomyRefreshResult.resolved_count === 1 ? 'y' : 'ies'} resolved
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
